import { useMemo } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONTRACT_SECTIONS } from '../config/contractSections';
import { CONTRACT_SECTION_VISUAL, CONTRACT_SECTION_ICON } from '../config/contractTokens';
import type { Contract } from '../contractTypes';
import { ColumnSections } from '../../../components/ui/sectionCard/SectionCard';
import type { SectionDef } from '../../../components/ui/sectionCard/types';
import type { ContractSectionKey } from '../config/contractTokens';
import { ContractStatusBadge } from '../../employees/components/ContractStatusBadge';
import { resolveContractStatus } from '../../employees/utils/contractStatus';
import theme from '../../../theme/theme';

const { containerPx, containerPt, containerPb, gap, gridCols } = theme.detailPage;

type ContractCardProps = {
  contract: Contract;
  /** Installation "Contract activation" option (`global.contratclosing='O'`). */
  activationOptionEnabled: boolean;
  /** Employee activation flag (`statutactive='O'`). */
  isActivated: boolean;
};

/**
 * Renders the full contract detail organised by functional blocks.
 *
 * All fields are always displayed — empty values show an empty label
 * (no hide-when-empty behaviour), as spec.
 *
 * Translations use the 'contract' namespace.
 */
export function ContractCard({
  contract,
  activationOptionEnabled,
  isActivated,
}: ContractCardProps) {
  const { t } = useTranslation(['contract', 'common']);

  // Render the `contract_current` field as the same status badge (labels + tags) as
  // the employee list, instead of a plain Yes/No. Injected here (not in the static
  // config) because it needs the page-level activation flags.
  const sections = useMemo<SectionDef<Contract, ContractSectionKey>[]>(
    () =>
      CONTRACT_SECTIONS.map(section => ({
        ...section,
        fields: section.fields.map(field =>
          field.key === 'contract_current'
            ? {
                ...field,
                render: (c: Contract) => (
                  <ContractStatusBadge
                    status={resolveContractStatus({
                      activationOptionEnabled,
                      hasCurrentContract: c.contract_current?.code === 'O',
                      isActivated,
                    })}
                  />
                ),
              }
            : field,
        ),
      })),
    [activationOptionEnabled, isActivated],
  );

  const col1 = sections.filter(s => s.column === 1);
  const col2 = sections.filter(s => s.column === 2);
  const col0 = sections.filter(s => s.column === 0);

  return (
    <Container maxWidth="xl" sx={{ px: containerPx, pt: containerPt, pb: containerPb }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: gap }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: gap,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: gap, minWidth: 0 }}>
            <ColumnSections<Contract, ContractSectionKey>
              sections={col1}
              data={contract}
              t={t}
              visualMap={CONTRACT_SECTION_VISUAL}
              iconMap={CONTRACT_SECTION_ICON}
              collapsible
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: gap, minWidth: 0 }}>
            <ColumnSections<Contract, ContractSectionKey>
              sections={col2}
              data={contract}
              t={t}
              visualMap={CONTRACT_SECTION_VISUAL}
              iconMap={CONTRACT_SECTION_ICON}
              collapsible
            />
          </Box>
        </Box>

        {col0.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: gap }}>
            <ColumnSections<Contract, ContractSectionKey>
              sections={col0}
              data={contract}
              t={t}
              visualMap={CONTRACT_SECTION_VISUAL}
              iconMap={CONTRACT_SECTION_ICON}
              collapsible
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}
